import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Button, message, notification, Switch, Table, Tooltip } from "antd";
import { CheckCircleOutlined, CheckOutlined, DownCircleOutlined, PlusCircleOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { compareStrings } from "@util/string";
import { compareBooleans } from "@util/boolean";
import { formatMaskedCPF, formatCPF } from "@util/cpf";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import useFormatter from "../../hooks/useFormatter";

import styles from "./_styles.less";
import ActionButton from "@components/action-button";
import { exportTableToExcel } from "@util/exportTable";
import { createHandleRow } from "@util/tableUtils";
import { showTotalWithSelection } from "@util/tableUtils";
import { atualizarSignatario } from "@/store/assinatura";
import useDispatchWithResult from "@/hooks/useDispatchWithResult";
import { formatCnpj } from "@/util/cnpj";

const CadastrosBaseTable = forwardRef(({ dataSource, loading, signTableRef, onClick, itemsList, isCadastro = false, selectedIds, onSelectedIdsChange, title, expandable, scroll, onRow, ...props }, ref) => {
  const dispatch = useDispatchWithResult();
  const divRef = useRef(null);
  const history = useHistory();
  const formatter = useFormatter();
  const [pageSize, setPageSize] = useState(10);
  const [sortingPaused, setSortingPaused] = useState(false);
  const [scrollY, setScrollY] = useState(scroll?.y);
  const { t } = useTranslation();
  const fireClick = (item) => (typeof onClick === "function" ? onClick(item) : undefined);
  const columns = [];

  const hasFieldInDataSource = (fieldName) => {
    return dataSource?.some((item) => fieldName in item);
  };

  const handlePreferencialClick = (prevSigner, checked) => {
    setSortingPaused(true);
    const signer = {
      signerID: prevSigner.id,
      signerData: {
        ...prevSigner,
        preferencial: checked,
      },
    };
    dispatch(atualizarSignatario(signer)).then(() =>
      notification.open({
        message: "Preferências alteradas!",
        icon: <CheckOutlined color="#009A00" />,
        duration: 2,
      })
    );
    setTimeout(() => {
      setSortingPaused(false);
    }, 600);
  };

  if (!isCadastro) {
    columns.push({
      render: (record) => {
        const isAdded = itemsList?.includes(record.id) ?? false;
        return ( 
        <Button type="text" onClick={() => fireClick(record)} icon={isAdded ? <CheckCircleOutlined style={{ fontSize: 15 }} /> : <PlusCircleOutlined style={{ fontSize: 15 }} />} disabled={isAdded} />
        );
      },
      width: 50,
      align: "center",
    });
  }

  if (hasFieldInDataSource("tipo")) {
    columns.push({
      title: t("Tipo"),
      dataIndex: "tipo",
      key: "tipo",
      width: 100,
      sorter: (a, b) => compareStrings(a.tipo, b.tipo),
      showSorterTooltip: false,
      defaultSortOrder: "descend",
      ellipsis: { showTitle: false },
      render: (text) => {
        const tipoMap = {
          socio: "Sócio(a)",
          procurador: "Procurador(a)",
          administrador: "Administrador(a)",
          representante: "Representante",
        };
        return tipoMap[text] || text || "-";
      },
    });
  }
  columns.push({
    title: t("Nome"),
    dataIndex: "nome",
    key: "nome",
    width: 200,
    sorter: (a, b) => compareStrings(a.nome, b.nome),
    showSorterTooltip: false,
    ellipsis: {
      showTitle: false
    },
    render: (text) => <span>{text || t("(não informado)")}</span>,
  });

  columns.push({
    title: t("Email"),
    dataIndex: "email",
    key: "email",
    width: 200,
    sorter: (a, b) => compareStrings(a.email, b.email),
    showSorterTooltip: false,
    ellipsis: { showTitle: false },
    render: (text) => <span>{text || t("(não informado)")}</span>,
  });

columns.push({
  title: t("CPF/CNPJ"),
  dataIndex: "cpf",
  key: "cpf",
  width: 130,
  sorter: (a, b) => compareStrings(a.cpf, b.cpf),
  showSorterTooltip: false,
  ellipsis: { showTitle: false },
  render: (text) => {
  if (!text) return "-";

  if (text.includes("*")) return formatMaskedCPF(text);

  const clean = text.replace(/\D/g, "");

  if (clean.length === 11) return formatCPF(clean);
  if (clean.length === 14) return formatCnpj(clean);

  return text;
},
});


  if (!hasFieldInDataSource("tipo"))
    columns.push({
      title: t("Preferencial"),
      dataIndex: "preferencial",
      key: "preferencial",
      width: 95,
      align: "center",
      ellipsis: { showTitle: false },
      sorter: sortingPaused ? false : (a, b) => compareBooleans(a.preferencial, b.preferencial),
      showSorterTooltip: false,
      defaultSortOrder: "descend",
      render: (_, record) => <Switch checked={record.preferencial} disabled={!isCadastro} onClick={(checkedVal) => handlePreferencialClick(record, checkedVal)} />,
    });

  const rowSelection =
    Array.isArray(selectedIds) && typeof onSelectedIdsChange === "function"
      ? {
          selectedRowKeys: selectedIds,
          onChange: onSelectedIdsChange,
        }
      : undefined;

  useImperativeHandle(
    ref,
    () => ({
      download: () =>
        exportTableToExcel({
          columns,
          dataSource,
          expandable,
          history,
          formatter,
        }),
    }),
    [columns, dataSource, expandable]
  );

  useEffect(() => {
    const updateHeight = () => {
      const drawerHeaderHeight = 300;
      const newHeight = window.innerHeight - drawerHeaderHeight;
      setScrollY(newHeight);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);

    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  return (
    <div ref={divRef} className={styles["table-wrapper"]}>
      <Table
        ref={signTableRef}
        columns={columns.filter((col) => !col.hidden)}
        dataSource={dataSource}
        expandable={expandable}
        loading={loading}
        title={title}
        pagination={{
          pageSize,
          showTotal: showTotalWithSelection(t, rowSelection),
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          onShowSizeChange: (_, size) => setPageSize(size),
        }}
        rowKey="id"
        rowSelection={rowSelection}
        scroll={{ x: "max-content", y: scrollY }}
        onRow={(record, rowIndex) => createHandleRow(record, rowIndex, onRow)}
        className={sortingPaused ? "smooth-sorting" : ""}
        {...props}
      />
    </div>
  );
});

function DownloadButton({ tableRef, disabled, ...props }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const handleClick = () => {
    setLoading(true);
    tableRef.current
      .download()
      .catch((err) => message.error(err))
      .finally(() => setLoading(false));
  };
  return <ActionButton disabled={disabled} noTitle title={t("Baixar Dados")} icon={<DownCircleOutlined />} loading={loading} onClick={handleClick} {...props} />;
}

CadastrosBaseTable.displayName = "SignatariosBaseTable";
CadastrosBaseTable.DownloadButton = DownloadButton;

CadastrosBaseTable.propTypes = {
  dataSource: PropTypes.array,
  loading: PropTypes.bool,
  signTableRef: PropTypes.object,
  onClick: PropTypes.func,
  showPreferencial: PropTypes.bool,
  showAddButton: PropTypes.bool,
  nameAsLink: PropTypes.bool,
  onSelectedIdsChange: PropTypes.func,
  selectedIds: PropTypes.arrayOf(PropTypes.number),
  title: PropTypes.func,
  scroll: PropTypes.object,
};

export default CadastrosBaseTable;
